import React, { useState } from 'react';
import ModeSelector from './components/ModeSelector';
import BasicModeInput from './components/BasicModeInput';
import AdvancedModeInput from './components/AdvancedModeInput';
import ActionButtons from './components/ActionButtons';
import Sidebar from './components/Sidebar';
import { saveDailyTarget, applyToAllChildren } from './api/dailyTarget';

const DEFAULT_TRANSLATIONS = {
  // Page header
  setDailyTargetFor: 'Set Daily Target for',
  configureGoals: 'Configure learning goals and targets for your child',

  // Breadcrumbs
  dashboard: 'Dashboard',
  children: 'Children',
  setDailyTarget: 'Set Daily Target',

  // Mode selector
  dailyLearningTargets: 'Daily Learning Targets',
  chooseTargetMode: 'Choose your preferred target setting mode',
  basicMode: 'Basic Mode',
  presetTargetOptions: 'Choose from preset options',
  advancedMode: 'Advanced Mode',
  customTargetNumber: 'Different targets per day',

  // Input labels
  xpTarget: 'XP Target',
  selectDailyTarget: 'Select the XP target for your child',
  day: 'Day',
  xpGoal: 'XP Goal',

  // XP option descriptions
  xp100Description: 'Under 10 minutes/day',
  xp200Description: 'About 15 minutes/day',
  xp500Description: 'About 30 minutes/day',
  xp750Description: 'About 45 minutes/day',
  xp1000Description: 'About 1 hour/day',

  // Days of week
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',

  // Action buttons
  backToDashboard: 'Back to Dashboard',
  resetToDefault: 'Reset to Default',
  saveTargetSettings: 'Save Target Settings',
  saving: 'Saving...',

  // Sidebar
  targetTips: 'Target Tips',
  tipAchievableGoals: 'Start with achievable goals',
  tipGradualIncrease: 'Gradually increase difficulty',
  tipWeekendDifferent: 'Weekend targets can be different',
  quickActions: 'Quick Actions',
  applyToAllChildren: 'Apply to All Children',

  // Upgrade modal
  upgradeModalTitle: 'Upgrade to Pro',
  upgradeModalDescription: 'Your child needs a Pro subscription to set daily goals higher than 200 XP.',
  upgradeModalClose: 'Close',
  upgradeModalUpgrade: 'Upgrade Now'
};

const DEFAULT_FREE_MAX_XP = 200;

const GoalSetting = ({
  childId,
  childName,
  childAvatar,
  childZodiacName,
  initialMode = 'basic',
  initialTargetXp,
  initialWeekdayXp,
  isUpdate = false,
  dashboardPath = '/',
  translations = {},
  defaultTargetXp, // Server-provided default, single source of truth
  hasSubscription = false,
  maxFreeXp = DEFAULT_FREE_MAX_XP,
  onUpgrade
}) => {
  // Calculate max XP based on subscription status
  // Subscribed kids have no limit (use Infinity), free kids limited to maxFreeXp
  const maxXp = hasSubscription ? Infinity : maxFreeXp;
  // Merge translations with defaults
  const t = (key) => translations[key] || DEFAULT_TRANSLATIONS[key] || key;

  // Build default weekday XP using server-provided default
  const defaultWeekdayXp = {
    mondayXp: defaultTargetXp,
    tuesdayXp: defaultTargetXp,
    wednesdayXp: defaultTargetXp,
    thursdayXp: defaultTargetXp,
    fridayXp: defaultTargetXp,
    saturdayXp: defaultTargetXp,
    sundayXp: defaultTargetXp
  };

  // State
  const [mode, setMode] = useState(initialMode);
  const [targetXp, setTargetXp] = useState(initialTargetXp ?? defaultTargetXp);
  const [weekdayXp, setWeekdayXp] = useState({
    ...defaultWeekdayXp,
    ...initialWeekdayXp
  });
  const [isSaving, setIsSaving] = useState(false);

  // Handlers
  const handleModeChange = (newMode) => {
    setMode(newMode);
  };

  const handleBack = () => {
    window.location.href = dashboardPath;
  };

  const handleReset = () => {
    setMode('basic');
    setTargetXp(defaultTargetXp);
    setWeekdayXp(defaultWeekdayXp);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const targetData = { mode, targetXp, weekdayXp };
      await saveDailyTarget(childId, targetData, isUpdate);
      // Redirect to dashboard on success
      window.location.href = dashboardPath;
    } catch (error) {
      console.error('Failed to save daily target:', error);
      setIsSaving(false);
    }
  };

  const handleApplyToAll = async () => {
    setIsSaving(true);
    try {
      const targetData = { mode, targetXp, weekdayXp };
      await applyToAllChildren(childId, targetData);
      // Redirect to dashboard on success
      window.location.href = dashboardPath;
    } catch (error) {
      console.error('Failed to apply to all children:', error);
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <a href={dashboardPath} className="text-decoration-none text-muted">
                {t('dashboard')}
              </a>
            </li>
            <li className="breadcrumb-item">
              <a href={dashboardPath} className="text-decoration-none text-muted">
                {t('children')}
              </a>
            </li>
            <li className="breadcrumb-item active fw-medium text-dark" aria-current="page">
              {t('setDailyTarget')}
            </li>
          </ol>
        </nav>

        {/* Page Header */}
        <div className="d-flex align-items-center mb-5">
          <span
            className={`zodiac-icon zodiac-icon-${childAvatar} zodiac-icon-md zodiac-icon-circle`}
            role="img"
            aria-label={childZodiacName}
          ></span>
          <div className="ms-4">
            <h1 className="h2 fw-bold mb-2" data-testid="page-title">
              {t('setDailyTargetFor')} {childName}
            </h1>
            <p className="text-muted mb-0">{t('configureGoals')}</p>
          </div>
        </div>

        <div className="row g-4">
          {/* Main Content */}
          <div className="col-lg-8">
            <div className="card border rounded-3">
              <div className="card-body p-4">
                {/* Show mode selector for all users */}
              <ModeSelector
                mode={mode}
                onModeChange={handleModeChange}
                t={t}
              />

                {/* Basic Mode Input */}
                {mode === 'basic' && (
                  <BasicModeInput
                    targetXp={targetXp}
                    onChange={setTargetXp}
                    t={t}
                    maxXp={maxXp}
                    onUpgrade={onUpgrade}
                  />
                )}

                {/* Advanced Mode Input - available for all users */}
                {mode === 'advanced' && (
                  <AdvancedModeInput
                    weekdayXp={weekdayXp}
                    onChange={setWeekdayXp}
                    t={t}
                    maxXp={maxXp}
                    childId={childId}
                    dashboardPath={dashboardPath}
                    onUpgrade={onUpgrade}
                  />
                )}

                <ActionButtons
                  onBack={handleBack}
                  onReset={handleReset}
                  onSave={handleSave}
                  isSaving={isSaving}
                  t={t}
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <Sidebar
            onApplyToAll={handleApplyToAll}
            t={t}
          />
        </div>
      </div>
    </div>
  );
};

export default GoalSetting;
